import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      setError("No se ha podido procesar la solicitud. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setEmail("");
    setSent(false);
    setError("");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={close}
          data-testid="forgot-password-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl p-7 text-white shadow-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(11,40,24,0.96), rgba(6,21,12,0.96))",
              border: "1px solid rgba(74,222,128,0.25)",
              backdropFilter: "blur(16px)",
            }}
          >
            <button
              onClick={close}
              className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors"
              data-testid="forgot-password-close"
            >
              <X size={18} />
            </button>

            {!sent ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center">
                    <Mail size={18} className="text-emerald-300" />
                  </div>
                  <div>
                    <h2 className="text-lg font-extrabold tracking-tight">¿Has olvidado tu contraseña?</h2>
                    <p className="text-emerald-100/60 text-xs">Te enviaremos un enlace seguro</p>
                  </div>
                </div>

                <p className="text-sm text-emerald-100/70 mb-5 leading-relaxed">
                  Introduce tu email corporativo. Si tienes una cuenta activa,
                  recibirás un enlace para crear una nueva contraseña. <span className="text-emerald-300/80">El enlace caduca en 1 hora.</span>
                </p>

                {error && (
                  <div data-testid="forgot-password-error" className="mb-4 p-3 bg-red-500/10 border border-red-400/30 rounded-lg text-red-300 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70" />
                    <input
                      data-testid="forgot-password-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoFocus
                      required
                      className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm
                                 text-white placeholder-white/30
                                 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60"
                      placeholder="tu@valorizasm.com"
                    />
                  </div>
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    type="submit"
                    disabled={loading}
                    data-testid="forgot-password-submit"
                    className="w-full flex items-center justify-center gap-2 mt-2
                               bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400
                               text-white font-bold py-3 rounded-xl
                               shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]
                               disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Enviar enlace <Send size={14} /></>
                    )}
                  </motion.button>
                </form>
              </>
            ) : (
              <div className="text-center py-3" data-testid="forgot-password-success">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.05 }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 mb-4"
                >
                  <CheckCircle2 size={32} className="text-emerald-300" />
                </motion.div>
                <h3 className="text-lg font-extrabold mb-2">Revisa tu correo</h3>
                <p className="text-sm text-emerald-100/70 leading-relaxed mb-5">
                  Si <strong className="text-emerald-200">{email}</strong> tiene una cuenta activa,
                  te hemos enviado un enlace para restablecer tu contraseña.
                </p>
                <p className="text-xs text-emerald-100/40">
                  ¿No te ha llegado? Revisa la carpeta de spam o vuelve a intentarlo en unos minutos.
                </p>
                <button
                  onClick={close}
                  data-testid="forgot-password-close-success"
                  className="mt-5 text-emerald-300 hover:text-emerald-200 font-semibold text-sm"
                >
                  Cerrar
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
